import { campaignTypeMap } from '../constants/constants';

export const getCpmForCampaignType = (org, campaignType) => {
  if (!org) return 7;

  // If pricing model is Percentage, return 0
  if (org.pricingModel?.model === 'Percentage') {
    return 0;
  }

  const typeString = typeof campaignType === 'number'
    ? campaignTypeMap[campaignType]
    : campaignType;

  if (org.pricingModel?.model === 'CPM' && org.pricingModel.cpmList?.length > 0) {
    const { cpmList } = org.pricingModel;

    const match = cpmList.find(item => item.type === typeString);

    if (match) {
      // ALWAYS use advertiserCPM (ignore hasContract flag)
      const value = match.advertiserCPM;
      return parseFloat(String(value).replace(/,/g, ''));
    }

    const firstEntry = cpmList[0];
    // ALWAYS use advertiserCPM (ignore hasContract flag)
    const value = firstEntry.advertiserCPM;
    return parseFloat(String(value).replace(/,/g, ''));
  }

  if (org.cpm) {
    return parseFloat(String(org.cpm).replace(/,/g, ''));
  }

  return 7;
};

/**
 * Get the contract CPM for a campaign type — what a CHANNEL_PARTNER org pays
 * the platform (the floor), used to bill CPs whose child-org campaigns are
 * priced at the customer's (retail) rate.
 *
 * @param {Object} org - Org with pricingModel.cpmList
 * @param {string|number} campaignType - Campaign type (DISPLAY, VIDEO, ... or 1, 8, ...)
 * @returns {number} - Contract CPM value (0 for Percentage model, 7 default)
 */
export const getContractCpmForCampaignType = (org, campaignType) => {
  if (!org) return 7;

  if (org.pricingModel?.model === 'Percentage') {
    return 0;
  }

  const typeString = typeof campaignType === 'number'
    ? campaignTypeMap[campaignType]
    : campaignType;

  if (org.pricingModel?.model === 'CPM' && org.pricingModel.cpmList?.length > 0) {
    const { cpmList } = org.pricingModel;

    const match = cpmList.find(item => item.type === typeString);
    const entry = match || cpmList[0];

    if (entry) {
      const value = entry.contractCPM ?? entry.advertiserCPM;
      return parseFloat(String(value).replace(/,/g, ''));
    }
  }

  if (org.cpm) {
    return parseFloat(String(org.cpm).replace(/,/g, ''));
  }

  return 7;
};

export const getDefaultCpm = (org) => {
  if (!org) return 7;

  // If pricing model is Percentage, return 0
  if (org.pricingModel?.model === 'Percentage') {
    return 0;
  }

  if (org.pricingModel?.model === 'CPM' && org.pricingModel.cpmList?.length > 0) {
    const { cpmList } = org.pricingModel;

    const displayEntry = cpmList.find(item => item.type === 'DISPLAY');
    if (displayEntry) {
      // ALWAYS use advertiserCPM (ignore hasContract flag)
      const value = displayEntry.advertiserCPM;
      return parseFloat(String(value).replace(/,/g, ''));
    }

    const firstEntry = cpmList[0];
    // ALWAYS use advertiserCPM (ignore hasContract flag)
    const value = firstEntry.advertiserCPM;
    return parseFloat(String(value).replace(/,/g, ''));
  }

  if (org.cpm) {
    return parseFloat(String(org.cpm).replace(/,/g, ''));
  }

  return 7;
};

export const isPercentageModel = (org) => {
  return org?.pricingModel?.model === 'Percentage';
};

/**
 * Get minimum CPM for campaign type validation
 * Returns the advertiser's minimum CPM requirement that must be enforced
 *
 * @param {Object} advertiser - Advertiser with pricingModel
 * @param {string|number} campaignType - Campaign type (DISPLAY, VIDEO, EXTERNAL or 1, 7, 8)
 * @returns {number} - Minimum CPM value (0 for Percentage model, 7 default)
 */
export const getMinimumCpmForCampaignType = (advertiser, campaignType) => {
  return getCpmForCampaignType(advertiser, campaignType);
};

/**
 * Get percentage value from advertiser's pricing model
 * @param {Object} advertiser - Advertiser with pricingModel
 * @returns {number|null} - Percentage value or null
 */
export const getPercentageForAdvertiser = (advertiser) => {
  if (!advertiser?.pricingModel || advertiser.pricingModel.model !== 'Percentage') {
    return null;
  }
  const auctionPlus = advertiser.pricingModel.auctionPlus;
  if (!auctionPlus || auctionPlus.advertiserPercentage === null) {
    return null;
  }
  return parseFloat(String(auctionPlus.advertiserPercentage).replace(/,/g, ''));
};

/**
 * Get minimum percentage for validation (mirrors getMinimumCpmForCampaignType)
 * @param {Object} advertiser - Advertiser with pricingModel
 * @returns {number} - Minimum percentage (0 if not Percentage model)
 */
export const getMinimumPercentageForAdvertiser = (advertiser) => {
  const percentage = getPercentageForAdvertiser(advertiser);
  return percentage !== null ? percentage : 0;
};

/**
 * Validate percentage override value
 * @param {number|null} value - Percentage to validate
 * @param {Object} advertiser - Advertiser with pricingModel
 * @returns {Promise} - Resolves if valid, rejects with error
 */
export const validatePercentageOverride = async (value, advertiser) => {
  if (value === '' || value === null || value === undefined) {
    return Promise.resolve();
  }
  if (!advertiser) {
    throw new Error('Please select an advertiser first');
  }
  const minimumPercentage = getMinimumPercentageForAdvertiser(advertiser);
  if (value < minimumPercentage) {
    throw new Error(`Percentage must be at least ${minimumPercentage.toFixed(2)}% (advertiser minimum)`);
  }
  if (value >= 100) {
    throw new Error('Percentage must be less than 100%');
  }
  return Promise.resolve();
};
